// src/utils/locationUtils.ts

/**
 * Location utilities.
 *
 * Resolution order:
 *
 * 1. If a cached "location" exists in localStorage → use it.
 * 2. ELSE, if Google Ads geotarget parameters are present:
 *    - loci → {loc_interest_ms}    (location-of-interest criterion ID)
 *    - locp → {loc_physical_ms}    (physical-location criterion ID)
 *    These are custom keys defined in your Google Ads tracking template.
 *    The client sends loci/locp to /api/geotarget-location, which looks up
 *    the Geo Criteria ID in a preprocessed JSON file on the server and
 *    returns an Ipapi-shaped object. If that works, cache it.
 * 3. ELSE, fall back to IP-based lookup via /api/ipapi (getIpapi()) and cache that.
 *
 * logClientEvent() is used selectively, on both success and error paths,
 * but not excessively (typically one event per resolution).
 */

import { logClientEvent } from '@/utils/logUtils'

// -----------------------------------------------------------------------------
// Types for ipapi-style object
// -----------------------------------------------------------------------------

export interface IpapiLocationMeta {
  geoname_id?: number | null
  capital?: string | null
  languages?: {
    code?: string
    name?: string
    native?: string
  }[]
  country_flag?: string
  country_flag_emoji?: string
  country_flag_emoji_unicode?: string
  calling_code?: string
  is_eu?: boolean
}

export interface IpapiTimeZone {
  id?: string
  current_time?: string
  gmt_offset?: number
  code?: string
  is_daylight_saving?: boolean
}

export interface IpapiCurrency {
  code?: string
  name?: string
  plural?: string
  symbol?: string
  symbol_native?: string
}

export interface IpapiConnection {
  asn?: number
  isp?: string
  sld?: string
  tld?: string
  carrier?: string
  home?: boolean
  organization_type?: string
  isic_code?: string
  naics_code?: string | number
}

/**
 * Shape of the ipapi-style object used throughout the app.
 * Many fields are optional to remain compatible with:
 *  - Actual ipapi responses from /api/ipapi
 *  - Synthetic objects built from Google Ads geotargets.
 */
export interface Ipapi {
  ip: string
  type?: string

  continent_code: string
  continent_name: string
  country_code: string
  country_name: string
  region_code: string
  region_name: string
  city: string
  zip: string

  latitude: number
  longitude: number

  msa?: string | null
  dma?: string | null
  radius?: string | null

  ip_routing_type?: string
  connection_type?: string

  location?: IpapiLocationMeta
  time_zone?: IpapiTimeZone
  currency?: IpapiCurrency
  connection?: IpapiConnection

  [key: string]: unknown
}

// -----------------------------------------------------------------------------
// IPAPI proxy call
// -----------------------------------------------------------------------------

/**
 * Calls the backend proxy (/api/ipapi), which in turn calls ipapi on the server.
 * The server resolves the client IP; the API key is never exposed in the browser.
 *
 * We log via logClientEvent() on both error and success paths, but only once
 * per call to keep network logging light.
 */
export const getIpapi = async (): Promise<Ipapi> => {
  try {
    const response = await fetch('/api/ipapi', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })

    const body = await response.json().catch(() => null)

    if (!response.ok) {
      let errorMessage = `Ipapi proxy request failed with status ${response.status}`

      if (body && typeof body === 'object' && 'error' in body) {
        const errorText = (body as { error?: string }).error
        if (errorText) {
          errorMessage += `: ${errorText}`
        }
      }

      logClientEvent('Ipapi proxy request failed', {
        status: response.status,
      })

      throw new Error(errorMessage)
    }

    const result = (body ?? {}) as Ipapi

    logClientEvent('Ipapi proxy request succeeded', {
      city: result.city,
      region: result.region_name,
      country: result.country_name,
    })

    return result
  } catch (error) {
    logClientEvent('Ipapi proxy request error', {
      error: error instanceof Error ? error.message : 'Unknown error',
    })

    throw new Error(
      error instanceof Error ? error.message : 'Failed to get Ipapi location'
    )
  }
}

// -----------------------------------------------------------------------------
// Helpers for canonical names (used by other parts of the app)
// -----------------------------------------------------------------------------

/**
 * Extracts a "state/region" portion from a canonical name like
 * "Aberdeen,North Carolina,United States" → "North Carolina".
 * If there is only one segment, returns that.
 */
export const getStateFromCanonicalName = (canonical: string): string => {
  const parts = canonical.split(',')
  return parts.length > 1 ? parts[1].trim() : parts[0].trim()
}

// -----------------------------------------------------------------------------
// Client-side helper to call the server geotarget resolver
// -----------------------------------------------------------------------------

/**
 * Attempts to derive location from Google Ads geotarget query parameters,
 * via the server API:
 *
 *   GET /api/geotarget-location?loci=...&locp=...
 *
 * loci and locp are custom keys in your Google Ads tracking template:
 *   - loci = {loc_interest_ms}    (location-of-interest criterion ID)
 *   - locp = {loc_physical_ms}    (physical-location criterion ID)
 *
 * The server looks up the criteria ID in a preprocessed JSON file and
 * returns an Ipapi-shaped object if found.
 *
 * This function returns:
 *   - Ipapi object on success
 *   - null if no loci/locp present, or if lookup fails
 */
const getLocationFromGeotargetApi = async (): Promise<Ipapi | null> => {
  if (typeof window === 'undefined') return null

  const url = new URL(window.location.href)
  const lociParam = url.searchParams.get('loci')
  const locpParam = url.searchParams.get('locp')

  if (!lociParam && !locpParam) {
    return null
  }

  const params = new URLSearchParams()
  if (lociParam) params.set('loci', lociParam)
  if (locpParam) params.set('locp', locpParam)

  try {
    const response = await fetch(`/api/geotarget-location?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })

    if (!response.ok) {
      // Only log that the lookup failed, no need to log body
      logClientEvent('Geotarget API lookup failed', {
        status: response.status,
      })
      return null
    }

    const body = (await response.json()) as Ipapi

    logClientEvent('Geotarget API lookup succeeded', {
      city: body.city,
      region: body.region_name,
      country: body.country_name,
    })

    return body
  } catch (error) {
    logClientEvent('Geotarget API lookup error', {
      error: error instanceof Error ? error.message : 'Unknown error',
    })
    return null
  }
}

// -----------------------------------------------------------------------------
// Main getLocation entry point
// -----------------------------------------------------------------------------

/**
 * Gets the user's location with simple localStorage caching.
 *
 * Resolution order:
 *
 * 1. Use cached "location" from localStorage if present.
 * 2. Else, try to derive via Google Ads geotarget params (loci/locp),
 *    by calling /api/geotarget-location on the server.
 * 3. Else, fall back to IP-based lookup via /api/ipapi.
 *
 * This function must be called in a browser environment.
 */
export const getLocation = async (): Promise<Ipapi> => {
  if (typeof window === 'undefined') {
    throw new Error('getLocation must be called in a browser environment')
  }

  const saved = window.localStorage.getItem('location')
  let location: Ipapi | undefined

  if (saved) {
    try {
      location = JSON.parse(saved) as Ipapi

      logClientEvent('Location loaded from cache', {
        city: location.city,
        region: location.region_name,
        country: location.country_name,
        sourceType: location.type ?? 'unknown',
      })
    } catch {
      // Corrupt or incompatible cache; clear it and move on.
      window.localStorage.removeItem('location')
      logClientEvent('Failed to parse cached location from localStorage')
      location = undefined
    }
  }

  // 1. If we have a cached location, use it.
  if (location) {
    return location
  }

  // 2. Try to derive location from Google Ads geotarget parameters via the server.
  const fromGeotarget = await getLocationFromGeotargetApi()
  if (fromGeotarget) {
    window.localStorage.setItem('location', JSON.stringify(fromGeotarget))
    // Success already logged inside getLocationFromGeotargetApi.
    return fromGeotarget
  }

  // 3. Fall back to IP-based lookup.
  const ipapiLocation = await getIpapi()
  window.localStorage.setItem('location', JSON.stringify(ipapiLocation))
  // Success already logged inside getIpapi.
  return ipapiLocation
}
